import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { IAuthState } from '../contexts/createAuthContext';
import { IUseLocalStorage } from '@monofront/types';
import {
  Collections,
  IUserPreferences,
  IUserPreferencesCRUD,
} from '../interfaces';
import {
  COLLECTIONS_PREFERENCES_DOCUMENT_NAME,
  PAYMENT_PREFERENCES_DOCUMENT_NAME,
  PREFERRED_PREFERENCES_COLLECTION_NAME,
} from '../constants/userPreferences';
import { areObjectsEqual } from '../utils';
import invariant from 'tiny-invariant';

type IUserPreferencesProviderProps = {
  /**
   * TODO: LF-1675 - shared useAuthentication hook
   */
  useAuthentication: () => IAuthState;
  useLocalStorage: IUseLocalStorage;
  userPreferencesCRUD: IUserPreferencesCRUD;
};

const DEFAULT_STATE: {
  [K in keyof IUserPreferences]-?: NonNullable<IUserPreferences[K]>;
} = {
  [COLLECTIONS_PREFERENCES_DOCUMENT_NAME]: {
    [PREFERRED_PREFERENCES_COLLECTION_NAME]: [],
  },
  [PAYMENT_PREFERENCES_DOCUMENT_NAME]: {
    doNotAskAgainUpdateSubPM: false,
  },
};

type IUserPreferencesContext = {
  collections: Collections;
  saveCollections: (collections: Collections) => Promise<void>;
  clear: () => void;
};

const DEFAULT_CONTEXT_STATE: IUserPreferencesContext = {
  ...DEFAULT_STATE,
  saveCollections: () => Promise.resolve(undefined),
  clear: () => {
    return;
  },
};

const UserPreferencesContext = createContext<IUserPreferencesContext>(
  DEFAULT_CONTEXT_STATE,
);

export const UserPreferencesProvider = ({
  useAuthentication,
  useLocalStorage,
  userPreferencesCRUD,
  children,
}: PropsWithChildren<IUserPreferencesProviderProps>) => {
  const { customer } = useAuthentication();
  const [storageValue, setStorageValue] = useLocalStorage(
    'lf_user_preferences',
    DEFAULT_STATE,
  );

  const [userPreferences, setUserPreferences] = useState({
    collections: storageValue.collections,
  });

  const clearLocalStorage = useCallback(
    () => setStorageValue(DEFAULT_STATE),
    [setStorageValue],
  );
  const clearContextState = useCallback(
    () => setUserPreferences(DEFAULT_STATE),
    [setUserPreferences],
  );

  const saveCollections = useCallback(
    async (collections: Collections) => {
      if (!customer) {
        setStorageValue({ ...storageValue, collections });
      } else {
        void userPreferencesCRUD.saveUserPreferences(
          customer.uid,
          COLLECTIONS_PREFERENCES_DOCUMENT_NAME,
          collections,
        );
      }
      setUserPreferences((prev) => ({
        ...prev,
        collections,
      }));
    },
    [customer, storageValue, userPreferencesCRUD],
  );

  useEffect(() => {
    const fetchUserPreferences = async () => {
      if (customer) {
        // Get remote preferences
        const data = await userPreferencesCRUD.createOrGetPreferences(
          customer.uid,
          COLLECTIONS_PREFERENCES_DOCUMENT_NAME,
        );

        if (!data) return;

        const mergePreferredCollection = () => {
          const set: Set<string> = new Set(storageValue.collections.preferred);

          if (data[PREFERRED_PREFERENCES_COLLECTION_NAME]) {
            for (const preferredCollection of data.preferred) {
              set.add(preferredCollection);
            }
          }

          return Array.from(set);
        };

        // Merge local and fetched data
        const userPreferences = {
          collections: {
            preferred: mergePreferredCollection(),
          },
        };

        // Update remote with local data and clean up locally
        if (
          !areObjectsEqual(storageValue.collections, DEFAULT_STATE.collections)
        ) {
          await userPreferencesCRUD.saveUserPreferences(
            customer.uid,
            COLLECTIONS_PREFERENCES_DOCUMENT_NAME,
            userPreferences.collections,
            { merge: true },
          );
          clearLocalStorage();
        }

        setUserPreferences((prev) => ({
          ...prev,
          ...userPreferences,
        }));
      }
    };

    void fetchUserPreferences();
  }, [customer, storageValue]);

  const value = useMemo(
    () => ({
      ...userPreferences,
      saveCollections,
      clear: clearContextState,
    }),
    [userPreferences, saveCollections, clearContextState],
  );

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
};

export const useUserPreferences = () => {
  const ctx = useContext(UserPreferencesContext);

  invariant(ctx, 'UserPreferencesContext should be present upper in the tree.');

  return ctx;
};
